import { useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "@/lib/auth/session";

type Stage = "email" | "code";

async function postJson(path: string, body: unknown): Promise<{ ok: boolean; message?: string }> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", "x-proly-client": "web" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { message?: unknown; error?: unknown };
  const message =
    typeof data.message === "string"
      ? data.message
      : typeof data.error === "string"
        ? data.error
        : undefined;
  return { ok: res.ok, message };
}

export function LoginForm({ next }: { next: string }) {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function sendCode() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await postJson("/api/auth/otp/request", { email });
      if (!res.ok) {
        setError(res.message ?? "Could not send the code.");
        return;
      }
      setStage("code");
      setNotice(`We sent a 6-digit code to ${email}.`);
    } catch {
      setError("Network error. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode() {
    setBusy(true);
    setError(null);
    try {
      const res = await postJson("/api/auth/otp/verify", { email, code });
      if (!res.ok) {
        setError(res.message ?? "Invalid or expired code.");
        return;
      }
      await refresh();
      navigate(next, { replace: true });
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const inputClass = "p-input w-full rounded-xl px-3.5 py-3 text-sm";
  const buttonClass = "p-btn-primary w-full rounded-xl py-3 px-4 text-sm font-medium";

  return (
    <div className="text-left">
      {stage === "email" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void sendCode();
          }}
          className="space-y-3"
        >
          <input
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
          <button type="submit" disabled={busy || !email} className={buttonClass}>
            {busy ? "Sending…" : "Email me a code"}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void verifyCode();
          }}
          className="space-y-3"
        >
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            required
            autoFocus
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className={`${inputClass} tracking-[0.4em] text-center`}
          />
          <button type="submit" disabled={busy || code.length < 6} className={buttonClass}>
            {busy ? "Verifying…" : "Verify & sign in"}
          </button>
          <button
            type="button"
            onClick={() => {
              setStage("email");
              setCode("");
              setNotice(null);
              setError(null);
            }}
            className="w-full text-xs text-[var(--p-muted)] hover:text-[var(--p-ink-soft)] hover:underline"
          >
            Use a different email
          </button>
        </form>
      )}
      {notice && <p className="mt-3 text-xs text-green-600 dark:text-green-400">{notice}</p>}
      {error && <p className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
